import React, { useEffect, useState } from 'react';
import SortableTree from '@nosferatu500/react-sortable-tree';
import { changeNodeAtPath, addNodeUnderParent, walk, removeNode} from '@nosferatu500/react-sortable-tree';
import SplitPane from 'react-split-pane';
import { useParams } from 'react-router-dom';
import '@nosferatu500/react-sortable-tree/style.css';
import { IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';

import Constants from '../../../utils/Constants'
import ServiceAPI from '../../../service/ServiceAPI';
import ContainerInputFields from './ContainerInputFields';
import ContainerModel from '../../../model/ContainerModel';
import ItemInputFields from './ItemInputFields';
import ItemModel from '../../../model/ItemModel';
import InventoryInputFields from './InventoryInputFields';
import InventoryModel from '../../../model/InventoryModel';
import { determineIfNodeCanBeDragged, determineIfNodeCanBeDropped } from './InventoryUtils';
import LoadingIndicator from '../../utils/LoadingIndicator';



const InventoryEdit = function(props) {
  const params = useParams();

  const [inventoryId, setInventoryId] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemChanged, setSelectedItemChanged] = useState(false);
  const [itemName, setItemName] = useState(null);
  const [itemDescription, setItemDescription] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(null);
  const [itemSealed, setItemSealed] = useState(0);
  const [itemInventoryGalleryId, setItemInventoryGalleryId] = useState(null);
  const [itemVersion, setItemVersion] = useState("0.0.0");


  const loadInventory = function() {
    if (inventoryData !== null) {
      return;
    }

    ServiceAPI.Inventory().read(params.inventoryId).then(
      (data) => {
        var treeData = [convertInventoryToTreeModel(data)];
        setInventoryData(treeData);
        setInventoryId(params.inventoryId);
      }
    )
  }
  


  const convertInventoryToTreeModel = function(inventory) {
    var fnConvertNodes = function(node) {
      node.title = node.name;
      node.subtitle = node.description;
      node.children = [];

      // Shows all the Locker containers in a inventory 
      // when it first loads
      if (node.type === Constants.InventoryType.Inventory) {
        node.expanded = true;
      }

      if (node.items && node.items.length > 0) {
        for (var index = 0; index < node.items.length; index++) {
          node.children.push(fnConvertNodes(node.items[index]));
        }
      }

      if (node.containers && node.containers.length > 0) {
        for (var index = 0; index < node.containers.length; index++) {
          node.children.push(fnConvertNodes(node.containers[index]));
        }

      }

      node.children.sort((a,b) => {
        return a.displayOrder > b.displayOrder ? 1 : -1;
      });

      return node;
    }

    return fnConvertNodes(inventory);
  };

  /**
   * 
   * @param {*} rowInfo 
   */
  const onClick_ViewTreeNodeDetails = function(rowInfo) {
    setSelectedItem(rowInfo);
    
    let isItem = rowInfo.node.type === Constants.InventoryType.Item;
    let isContainer = rowInfo.node.type === Constants.InventoryType.Container;
    let isInventory = rowInfo.node.type === Constants.InventoryType.Inventory;

    setItemName(rowInfo.node.name);
    setItemDescription(rowInfo.node.description == null ? "" : rowInfo.node.description);

    if (isInventory) {
      setItemVersion(rowInfo.node.version);
    }

    if (isItem) {
      setItemQuantity(rowInfo.node.quantity);
    }

    if (isContainer) {
      setItemSealed(rowInfo.node.sealed);
    }

    if (isItem || isContainer) {
      setItemInventoryGalleryId(rowInfo.node.inventoryGalleryId);
      console.debug(`onClick_ViewTreeNodeDetails: ${rowInfo.node.inventoryGalleryId}`);
    }
  }

  /**
   * 
   * @param {*} moveInfo 
   */
  const updateDisplayOrder = function(moveInfo, debugOn = false) {

    debugOn && console.debug(
      `Node: ${moveInfo.node.name} (${moveInfo.node.id}) / Path: ${moveInfo.path} / From: ${moveInfo.prevPath} / To: ${moveInfo.nextPath} / New Parent: ${moveInfo.nextParentNode?.name} (${moveInfo.nextParentNode?.id}) `
    );

    debugOn && console.debug("MoveInfo:", moveInfo);

    /*
      x. updateDisplayOrder is called for indivudal node movements.
      x. nodes with children, only result in one updateDisplayOrder execution (for the moved node)
      x. container API UPDATE does not update the ParentContainerId. Only MOVE API will.
      x. item API UPDATE will update the containerId when the UPDATE API is called.

      1. Find NEW parent ID

      2. Check if Parent is changing.

      3. Check if Container Type is being shifted, 
      
        3.1 If parent has changed, call the Move Container API

      4. Check if Item Type is being shifted,

        4.1 If parent has changed, update the containerId field on the Node

      5. Walk the tree to update changed display Ids (currenlty does it by assuming ALL have changed)
    */


    let newParentId = moveInfo.nextParentNode.id;
    let isContainer = moveInfo.node.type === Constants.InventoryType.Container;
    let isItem = moveInfo.node.type === Constants.InventoryType.Item;
    
    let fromPath = moveInfo.prevPath;
    let toPath = moveInfo.nextPath;
    
    // Check if the Node actually moved! If it didnt than exit function.
    if (fromPath.join("-") === toPath.join("-")) {
      debugOn && console.log("Node has not moved from original position. Exit displayOrder update.");
      return;
    }

    // Check if the Parent for the node has changed.
    fromPath.pop();
    toPath.pop();

    let isParentTheSame = fromPath.join("-") === toPath.join("-");


    debugOn && console.debug(`newParentId: ${newParentId} / isContainer: ${isContainer} / isItem: ${isItem} / isParentTheSame: ${isParentTheSame}`);

    // If the Parent has changed we need to do work to ensure that the Database will be updated with the 
    // new Parent Container Id. THe tasks to do are different depending on if the node is a Container or a Item.
    if (!isParentTheSame) {

      switch (moveInfo.node.type) {

        case Constants.InventoryType.Container:
          // For containers we must use the Move API to change the parent container Id. 
          ServiceAPI.Container().moveContainer(moveInfo.node.id, newParentId).then( (json) => { 
            // Do nothing in the callback by design.
          });

          break;

        case Constants.InventoryType.Item:
          // For a Item node we must update the node ContainerId value, as when 
          // the UPDATE API is called, it will updated the ContainerId.
          changeNodeAtPath({
            treeData: inventoryData,
            path: moveInfo.prevPath,
            newNode: (treeNode) => {
              treeNode.node.containerId = newParentId;
              return treeNode;
            },
            getNodeKey: ({ treeIndex }) => treeIndex
          });

          break;
      }
    }

    let displayOrderUpdate = [];

    /* 
      TODO: This function build a array of all display updates. Will be used in the future 
      to switch this update from a call per item, to a single call for inventory.
    */
    let fnBuildDIsplayOrderUpdateArray = (treeNode) => {
      let displayUpdate = {
        nodeType: treeNode.node.type,
        name: treeNode.node.name,
        id: treeNode.node.id,
        displayOrder: treeNode.treeIndex
      }

      displayOrderUpdate.push(displayUpdate);
    };

    let fnPersitIndex = (treeNode) => {
      fnBuildDIsplayOrderUpdateArray(treeNode);

      let node = treeNode.node;
      node.displayOrder = treeNode.treeIndex;  
      
      debugOn && console.debug("UpdateDisplayOrder: TreeNode", treeNode);

      let parentContainerId = null;

      // If the ParentNode is undefined, than the treendoe that is 
      // passed in is the Inventory node. No changes are needed for 
      // display order updates.
      if (treeNode.parentNode === undefined) {
        return;
      }

      // If the parent node is a inventory, than this node is a Locker node.
      // The Parent Contaienr Id should be NULL
      if (treeNode.parentNode.type === Constants.InventoryType.Inventory) {
        parentContainerId = null;
      } else {
        parentContainerId = treeNode.parentNode.id;
      }

      debugOn && console.debug("UpdateDisplayOrder: parentContainerId", parentContainerId);

      switch (node.type) {

        case Constants.InventoryType.Container:
          node.parentContainerId = parentContainerId;

          ServiceAPI.Container().updateContainer(node).then( 
            (json) => { debugOn && console.log("Update Complete:", json)} 
          );
          break;

        case Constants.InventoryType.Item:
          let itemModel = new ItemModel(node);

          itemModel.setContainerId(parentContainerId);

          ServiceAPI.Item().updateItem(itemModel).then( 
            (json) => { debugOn && console.log("Update Complete:", json)} 
          );
          break;
      }
    };


    walk({
      treeData: moveInfo.treeData,
      getNodeKey: ({ treeIndex }) => treeIndex,
      callback: fnPersitIndex,
      ignoreCollapsed: false
    });
    debugOn && console.debug("DisplayOrderUpdates:", displayOrderUpdate);
  }
  
  /**
   * 
   * @param {*} event 
   */
  const addNewItemToContainer = (event) => {
    let newItem = new ItemModel();

    newItem.setName("New Item");
    newItem.setDescription("");
    newItem.setQuantity(1);
    newItem.setContainerId(selectedItem.node.id);
    
    let treeNode = newItem.data;
    treeNode.title = newItem.getName();

    let newTreeModel = addNodeUnderParent({
      treeData: inventoryData,
      newNode: treeNode,
      parentKey: selectedItem.treeIndex,
      getNodeKey: ({ treeIndex }) => treeIndex,
      ignoreCollapsed: true,
      expandParent: true,
      addAsFirstChild: false
    });

    setInventoryData(newTreeModel.treeData);
    
    let newPath = selectedItem.path.slice();
    newPath.push(newTreeModel.treeIndex);

    // Set the display order to the items tree index
    newItem.setDisplayOrder( newTreeModel.treeIndex );

    ServiceAPI.Item().createItem(newItem).then( 
      (json) => { 
        changeNodeAtPath({
          treeData: newTreeModel.treeData,
          path: newPath,
          newNode: (treeNode) => treeNode.node.id = json.id,
          getNodeKey: ({ treeIndex }) => treeIndex
        });
      } 
    );
  }

  /**
   * Creates a new container based on the supplied container item. Shoudld only be 
   * called from addNewHeadingContainerToContainer and addNewContainerToContainer
   * 
   * @param {*} container 
   */
  const addNewContainer = (newContainer) => {
    newContainer.setInventoryId(inventoryId);
    newContainer.setParentContainerId(selectedItem.node.type !== Constants.InventoryType.Inventory ? selectedItem.node.id : null)
    newContainer.setDescription("");
    newContainer.setSealed(0);

    let treeNode = newContainer.data;
    treeNode.title = newContainer.getName();

    let newTreeModel = addNodeUnderParent({
      treeData: inventoryData,
      newNode: treeNode,
      parentKey: selectedItem.treeIndex,
      getNodeKey: ({ treeIndex }) => treeIndex,
      ignoreCollapsed: true,
      expandParent: true,
      addAsFirstChild: false
    });

    setInventoryData(newTreeModel.treeData)

    let newPath = selectedItem.path.slice();
    newPath.push(newTreeModel.treeIndex);

    // Set the display order to the items tree index
    newContainer.setDisplayOrder( newTreeModel.treeIndex );

    ServiceAPI.Container().createContainer(newContainer).then( 
      (json) => { 
        changeNodeAtPath({
          treeData: newTreeModel.treeData,
          path: newPath,
          newNode: (treeNode) =>  treeNode.node.id = json.id ,
          getNodeKey: ({ treeIndex }) => treeIndex
        });
      } 
    );
  }

  /**
   * Creates a new Heading Container item
   * @param {*} event 
   */
  const addNewHeadingContainerToContainer = (event) => {
    let newContainer = new ContainerModel();

    newContainer.setName("New Heading");
    newContainer.setHeading(1);

    addNewContainer(newContainer);
  }

  /**
   * Creates a new Container item
   * @param {*} event 
   */
  const addNewContainerToContainer = (event) => {
    let newContainer = new ContainerModel();

    newContainer.setName("New Container");
    newContainer.setHeading(0);

    addNewContainer(newContainer);
  }

  /**
   * 
   * @param {*} event 
   */
  const updateFieldValue = (event) => {
    var field = event.target.getAttribute('data-field');
    var updateFn = null;

    switch (field) {
      
      case "name":
        setItemName(event.target.value);
        updateFn = (treeNode) => {
          treeNode.node.name = event.target.value;
          treeNode.node.title = event.target.value;
          return treeNode;
        }
        break;
      
      case "description":
        setItemDescription(event.target.value);
        updateFn = (treeNode) => {
          treeNode.node.description = event.target.value;
          return treeNode;
        }
        break;

      case "quantity":
        setItemQuantity(event.target.value);
        updateFn = (treeNode) => {
          treeNode.node.quantity = event.target.value;
          return treeNode;
        }
        break;

      case "inventoryGalleryId":
        setItemInventoryGalleryId(event.target.value);
        updateFn = (treeNode) => {
          treeNode.node.inventoryGalleryId = event.target.value;
          return treeNode;
        }
        break;

      case "sealed":
        let isSealed = event.target.checked === true ? 1 : 0;
        setItemSealed(isSealed);
        updateFn = (treeNode) => {
          treeNode.node.sealed = isSealed
          return treeNode;
        }
        break;

      case "version":
        setItemVersion(event.target.value);
        updateFn = (treeNode) => {
          treeNode.node.version = event.target.value
          return treeNode;
        }
        break;
    }
    
    changeNodeAtPath({
      treeData: inventoryData,
      path: selectedItem.path,
      newNode: updateFn,
      getNodeKey: ({ treeIndex }) => treeIndex
    });

    setSelectedItemChanged(true);
  }

  /**
   * 
   */
  const saveUpdatedItem = (event) => {
    if (selectedItemChanged === false && event?.overideChangeFlag === undefined) {
      return;
    }
    
    setSelectedItemChanged(false);
    
    var selected = selectedItem.node;      

    switch (selected.type) {

      case Constants.InventoryType.Inventory:
        let inventoryModel = new InventoryModel(selected);

        ServiceAPI.Inventory().updateInventory(inventoryModel).then(
          (json) => { console.log("Update Complete:", json); }
        );
        break;

      case Constants.InventoryType.Container:
        ServiceAPI.Container().updateContainer(selected).then( 
          (json) => { console.log("Update Complete:", json)} 
        );
        break;

      case Constants.InventoryType.Item:
        let itemModel = new ItemModel(selected);

        ServiceAPI.Item().updateItem(itemModel).then( 
          (json) => { console.log("Update Complete:", json)} 
        );
        break;
    }
    
  }

  /**
   * 
   */
  const deleteItem = (e) => {
    var selected = selectedItem.node;

    let outcome = removeNode({
      treeData: inventoryData,
      path: selectedItem.path,
      getNodeKey: ({ treeIndex }) => treeIndex
    });

    setInventoryData(outcome.treeData);
    setSelectedItem(null);

    ServiceAPI.Item().deleteItem(selected.id).then( 
      (json) => { console.log("Delete Item Complete:", json)} 
    );
  }

  /**
   * 
   */
  const deleteContainer = (e) => {
    var selected = selectedItem.node;
    console.log(selectedItem);
    let outcome = removeNode({
      treeData: inventoryData,
      path: selectedItem.path,
      getNodeKey: ({ treeIndex }) => treeIndex
    });
    
    setInventoryData(outcome.treeData);
    setSelectedItem(null);

    ServiceAPI.Container().deleteContainer(selected.id).then( 
      (json) => { console.log("Delete Container Complete:", json)} 
    );
  }

  /**
   * 
   */
  const render = function() {
    return (
      <div className="main">
        <LoadingIndicator isOpen={inventoryData === null} />

        <SplitPane split="vertical" minSize="60%" style={{position: "static"}} >
          <div style={{ height: '99%' }}>
            { renderInventoryTree() }
          </div>
          <div>
            { renderSelectedItemDetails() }
          </div>
        </SplitPane>
      </div>
    )
  };

  const renderInventoryTree = function() {
    if ( inventoryData === null ) {
      return null;
    }

    return (
      <SortableTree 
        treeData={inventoryData} 
        onChange={treeData => setInventoryData( treeData )}
        onMoveNode={ updateDisplayOrder }
        canDrop={ determineIfNodeCanBeDropped }
        canDrag={ determineIfNodeCanBeDragged }
        generateNodeProps={ rowInfo => ({
          buttons: [
            <IconButton
              onClick={() => onClick_ViewTreeNodeDetails(rowInfo)}
            >
              <EditIcon />
            </IconButton>
          ],
        })}
      />
    );
  }

  const renderSelectedItemDetails = function() {
    if (selectedItem === null) {
      return <div/>;
    }

    let isInventory = selectedItem.node.type === Constants.InventoryType.Inventory;
    let isItem = selectedItem.node.type === Constants.InventoryType.Item;
    let isContainer = selectedItem.node.type === Constants.InventoryType.Container;
    let isParent = selectedItem.node.children !== undefined && selectedItem.node.children.length > 0;
    let isHeadingContainer = isContainer && selectedItem.node.heading === 1 ? 1 : 0;

    let inventoryFields = isInventory ? (
      <InventoryInputFields 
        itemName={ itemName }
        itemVersion={ itemVersion }
        handleOnNewContainer={ addNewHeadingContainerToContainer }
        handleOnChange={ updateFieldValue }
        handleOnBlur={ saveUpdatedItem }
      />
    ) : null;

    let itemFields = isItem ? (
      <ItemInputFields
        itemName={ itemName } 
        itemDescription={ itemDescription }
        itemQuantity={ itemQuantity}
        itemInventoryGalleryId={ itemInventoryGalleryId }
        handleOnChange={ updateFieldValue } 
        handleOnBlur={ saveUpdatedItem } 
        handleOnDelete={ deleteItem }
      />
    ) : null;

    let continerInput = isContainer ? (
      <ContainerInputFields 
        itemName={ itemName } 
        itemDescription={ itemDescription }
        itemSealed= { itemSealed }
        itemInventoryGalleryId={ itemInventoryGalleryId }
        itemIsHeadingContainer={ isHeadingContainer }
        handleOnChange={ updateFieldValue } 
        handleOnBlur={ saveUpdatedItem } 
        handleOnNewHeading={ addNewHeadingContainerToContainer }
        handleOnNewItem={ addNewItemToContainer }
        handleOnNewContainer={ addNewContainerToContainer }
        handleOnDelete={ isParent ? null : deleteContainer }
      />
    ) : null;
    

    return (
      <div style={{margin: '.5em'}}>
        { inventoryFields }
        { continerInput }
        { itemFields }
      </div>
    );
  }

  //const render

  useEffect(() => {
    loadInventory();
  }, []);
  

  return render();
};

export default InventoryEdit;